import { Component, HostBinding, Input, ViewChild, AfterViewInit, ElementRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NAV_OPEN_CLOSE_ANIMATION_DURATION } from '@vendasta/galaxy/nav/src/nav.component';

type PreviewSize = 'desktop' | 'tablet' | 'mobile';

@Component({
  selector: 'email-editor-preview',
  templateUrl: './email-editor-preview.component.html',
  styleUrls: ['./email-editor-preview.component.scss'],
})
export class EmailEditorPreviewComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'email-editor-preview';

  @Input() loadingIndicator = false;
  @Input() previewIsMobile = false;

  @Input()
  public set html(renderedTemplate: string) {
    this._html = renderedTemplate;
    this.updateIFramePreview(this._html);
  }

  @ViewChild('iframePreview', { static: true }) iframePreview: ElementRef;

  element = this.elementRef.nativeElement;
  emailPreviewWidth: string;
  subscriptions: Subscription[] = [];
  _html: string;

  showDesktopButton = true;
  showDevicePreviewSizeButtons = true;
  resizeObserver: ResizeObserver;

  previewSizeSelected: PreviewSize = 'desktop';

  constructor(private elementRef: ElementRef) {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = Array.isArray(entries) ? entries[0] : entries;
        const componentWidth = entry.contentRect.width;

        const previousShowDesktopButton = this.showDesktopButton;
        this.showDesktopButton = componentWidth > 600;

        if (!this.showDesktopButton && previousShowDesktopButton && this.previewSizeSelected === 'desktop') {
          this.previewSizeClicked('tablet');
        }
        this.showDevicePreviewSizeButtons = componentWidth > 320;
      });
    }
  }

  ngAfterViewInit(): void {
    window.setTimeout(() => this.resizeObserver.observe(this.element), NAV_OPEN_CLOSE_ANIMATION_DURATION);
    // hacky, but nativeElement.contentDocument will be missing sometimes without this.
    window.setTimeout(() => this.updateIFramePreview(this._html), 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }

  previewSizeClicked(value: PreviewSize): void {
    switch (value) {
      case 'desktop':
        this.emailPreviewWidth = '100%';
        this.previewSizeSelected = 'desktop';
        break;
      case 'tablet':
        this.emailPreviewWidth = '600px';
        this.previewSizeSelected = 'tablet';
        break;
      case 'mobile':
        this.emailPreviewWidth = '320px';
        this.previewSizeSelected = 'mobile';
        break;
      default:
        this.emailPreviewWidth = '';
        break;
    }
  }

  setIframeHeightOnMobile(): void {
    if (this.previewIsMobile) {
      const iframeBody = this.iframePreview.nativeElement.contentWindow.document.body;
      if (iframeBody) {
        this.iframePreview.nativeElement.style.height = iframeBody.offsetHeight + 40 + 'px';
      }
    }
  }

  updateIFramePreview(renderedTemplate: string): void {
    // We have to be careful here as the iframe
    // and contents may not be available
    if (this.previewIsMobile || !renderedTemplate || !this.iframePreview) {
      return;
    }
    const contentWindow = this.iframePreview.nativeElement.contentWindow;
    if (!contentWindow) {
      return;
    }

    const contentDocument = this.iframePreview.nativeElement.contentDocument;
    if (!contentDocument) {
      return;
    }

    const scrollX = contentWindow.window?.scrollX;
    const scrollY = contentWindow.window?.scrollY;

    contentDocument.open();
    contentDocument.write(renderedTemplate);
    contentDocument.close();
    // firefox does not preserve the scroll, so we have
    // to set it manually
    this.iframePreview.nativeElement.contentWindow.scrollTo(scrollX, scrollY);
  }
}
